import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { transfertsAPI, boutiquesAPI, clientsAPI } from '../../services/api';
import {
  ArrowLeftRight, Search, X, Check, Clock, Package, MapPin,
  ChevronRight, RefreshCw, Plus, AlertCircle, Truck, History,
  CheckCircle, Send, Filter, ArrowRight, UserCircle, CreditCard
} from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

const STATUTS = {
  en_attente: { label: 'En attente',   cls: 'bg-amber-100 text-amber-800 border-amber-200',   icon: Clock },
  valide:     { label: 'En transit',   cls: 'bg-blue-100 text-blue-800 border-blue-200',       icon: Truck },
  recu:       { label: 'Reçu',         cls: 'bg-green-100 text-green-800 border-green-200',    icon: CheckCircle },
  annule:     { label: 'Annulé',       cls: 'bg-red-100 text-red-800 border-red-200',          icon: X },
};

function StatutBadge({ statut }) {
  const cfg = STATUTS[statut] || STATUTS.en_attente;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
}

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Recherche client inline ──────────────────────────────────────────────────

function ClientSearchInline({ value, onChange }) {
  // value = { id, nom, telephone } | null
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await clientsAPI.getAll();
        const all = Array.isArray(res.data) ? res.data : [];
        const q = query.toLowerCase();
        setResults(all.filter(c =>
          (c.nom && c.nom.toLowerCase().includes(q)) ||
          (c.telephone && c.telephone.includes(q))
        ).slice(0, 8));
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const select = (c) => {
    onChange({ id: c.id, nom: c.nom, telephone: c.telephone });
    setQuery('');
    setOpen(false);
  };

  const clear = () => { onChange(null); setQuery(''); };

  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
        <UserCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-800 truncate">{value.nom}</div>
          {value.telephone && <div className="text-xs text-gray-500">{value.telephone}</div>}
        </div>
        <button onClick={clear} className="p-0.5 text-gray-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        {loading && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />}
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Rechercher un client par nom ou tél..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-400"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {results.map(c => (
            <button key={c.id} onClick={() => select(c)}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-purple-50 text-left">
              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-purple-600">{(c.nom || '?')[0].toUpperCase()}</span>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{c.nom}</div>
                {c.telephone && <div className="text-xs text-gray-400">{c.telephone}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.length >= 2 && !loading && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-sm text-gray-400">
          Aucun client trouvé
        </div>
      )}
    </div>
  );
}

// ─── composant principal ──────────────────────────────────────────────────────

export default function TransfertsAvance() {
  const ctx = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}'); }
    catch { return {}; }
  }, []);

  const isCentral  = ctx.type === 'central';
  const myBoutiqueId = ctx.boutique_id ? parseInt(ctx.boutique_id) : null;

  return isCentral
    ? <CentralView />
    : <BoutiqueView myBoutiqueId={myBoutiqueId} myBoutiqueName={ctx.boutique_nom} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  VUE CENTRALE
// ═══════════════════════════════════════════════════════════════════════════════

function CentralView() {
  const [tab, setTab]               = useState('en_attente'); // en_attente | valide | historique | nouveau
  const [transferts, setTransferts] = useState([]);
  const [boutiques, setBoutiques]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);   // transfert en cours de traitement
  const [showValidModal, setShowValidModal] = useState(false);
  const [showNouveauModal, setShowNouveauModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        transfertsAPI.getAll(),
        boutiquesAPI.getAll(),
      ]);
      setTransferts(Array.isArray(tRes.data) ? tRes.data : []);
      setBoutiques(Array.isArray(bRes.data) ? bRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === 'historique') return transferts.filter(t => ['recu','annule'].includes(t.statut));
    return transferts.filter(t => t.statut === tab);
  }, [transferts, tab]);

  const counts = useMemo(() => ({
    en_attente: transferts.filter(t => t.statut === 'en_attente').length,
    valide:     transferts.filter(t => t.statut === 'valide').length,
    recu:       transferts.filter(t => t.statut === 'recu').length,
  }), [transferts]);

  const handleValider = (t) => { setSelected(t); setShowValidModal(true); };

  const handleRecevoir = async (t) => {
    if (!window.confirm(`Confirmer la réception de la pièce "${t.bijou_nom || 'cette pièce'}" ?`)) return;
    try {
      await transfertsAPI.recevoir(t.id);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const handleAnnuler = async (t) => {
    if (!window.confirm('Annuler ce transfert ?')) return;
    try {
      await transfertsAPI.annuler(t.id);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const TABS = [
    { key: 'en_attente', label: 'En attente', count: counts.en_attente, icon: Clock,        color: 'amber' },
    { key: 'valide',     label: 'En transit',  count: counts.valide,     icon: Truck,        color: 'blue'  },
    { key: 'historique', label: 'Historique',  count: counts.recu,       icon: History,      color: 'green' },
  ];

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-2.5 rounded-xl shadow">
            <ArrowLeftRight className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestion des transferts</h2>
            <p className="text-sm text-gray-500">Validation et suivi des mouvements de bijoux</p>
          </div>
        </div>
        <button
          onClick={() => setShowNouveauModal(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 shadow-sm transition-all"
        >
          <Plus className="h-4 w-4" />
          Nouveau transfert
        </button>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'En attente de traitement', val: counts.en_attente, color: 'amber',  icon: Clock },
          { label: 'En transit',               val: counts.valide,     color: 'blue',   icon: Truck },
          { label: 'Reçus ce mois',            val: counts.recu,       color: 'green',  icon: CheckCircle },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-xl p-4 flex items-center gap-4`}>
              <div className={`bg-${s.color}-100 p-3 rounded-lg`}>
                <Icon className={`h-6 w-6 text-${s.color}-600`} />
              </div>
              <div>
                <div className={`text-3xl font-bold text-${s.color}-700`}>{s.val}</div>
                <div className={`text-xs text-${s.color}-600 font-medium`}>{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Tabs ── */}
      <div className="overflow-x-auto">
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {t.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                tab === t.key ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-600'
              }`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>
      </div>

      {/* ── Liste ── */}

      {/* Mobile cards */}
      {!loading && filtered.length > 0 && (
        <div className="md:hidden space-y-3">
          {filtered.map(t => (
            <div key={t.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
              <div className="flex justify-between items-start mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-gray-900 text-sm truncate">
                    {t.bijou_nom || <span className="italic text-gray-400">Sans pièce assignée</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{fmt(t.date_demande)}</div>
                </div>
                <StatutBadge statut={t.statut} />
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                <MapPin className="h-3 w-3 text-red-400 flex-shrink-0" />
                <span>{t.boutique_source_nom || 'Central'}</span>
                <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <MapPin className="h-3 w-3 text-green-400 flex-shrink-0" />
                <span>{t.boutique_destination_nom || 'Central'}</span>
              </div>
              {tab !== 'historique' && (
                <div className="flex gap-2">
                  {t.statut === 'en_attente' && (
                    <>
                      <button
                        onClick={() => handleValider(t)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Traiter
                      </button>
                      <button
                        onClick={() => handleAnnuler(t)}
                        className="px-3 py-2 text-red-400 border border-red-200 rounded-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {t.statut === 'valide' && (
                    <button
                      onClick={() => handleRecevoir(t)}
                      className="w-full flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Réception confirmée
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="h-6 w-6 text-purple-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <Package className="h-8 w-8 mb-2" />
            <p className="text-sm">Aucun transfert {tab === 'en_attente' ? 'en attente' : tab === 'valide' ? 'en transit' : 'dans l\'historique'}</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Bijou / Demande</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Trajet</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                {tab !== 'historique' && (
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(t.date_demande)}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-gray-900 text-sm">
                      {t.bijou_nom || <span className="italic text-gray-400">Demande sans pièce assignée</span>}
                    </div>
                    {t.reference_article && (
                      <div className="text-xs text-gray-400 mt-0.5">Réf. {t.reference_article}</div>
                    )}
                    {t.commentaire && (
                      <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 mt-1 inline-block">
                        📝 {t.commentaire}
                      </div>
                    )}
                    {t.client_nom && (
                      <div className="mt-1">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${t.vendu_avance ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                          <UserCircle className="h-3 w-3" />
                          {t.client_nom}
                          {t.vendu_avance ? ' · Payé ✓' : ' · À récupérer'}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1 text-gray-600">
                        <MapPin className="h-3.5 w-3.5 text-red-400" />
                        {t.boutique_source_nom || 'Central'}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <span className="flex items-center gap-1 text-gray-600">
                        <MapPin className="h-3.5 w-3.5 text-green-400" />
                        {t.boutique_destination_nom || 'Central'}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <StatutBadge statut={t.statut} />
                  </td>
                  {tab !== 'historique' && (
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {t.statut === 'en_attente' && (
                          <>
                            <button
                              onClick={() => handleValider(t)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <Send className="h-3.5 w-3.5" />
                              Traiter
                            </button>
                            <button
                              onClick={() => handleAnnuler(t)}
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                              title="Annuler"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {t.statut === 'valide' && (
                          <button
                            onClick={() => handleRecevoir(t)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            Réception confirmée
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Empty state for mobile */}
      {!loading && filtered.length === 0 && (
        <div className="md:hidden flex flex-col items-center justify-center h-32 text-gray-400 bg-white rounded-xl border border-gray-200">
          <Package className="h-8 w-8 mb-2" />
          <p className="text-sm">Aucun transfert {tab === 'en_attente' ? 'en attente' : tab === 'valide' ? 'en transit' : 'dans l\'historique'}</p>
        </div>
      )}
      {loading && (
        <div className="md:hidden flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 text-purple-500 animate-spin" />
        </div>
      )}

      {/* ── Modal validation (traiter une demande) ── */}
      {showValidModal && selected && (
        <ValidModal
          transfert={selected}
          boutiques={boutiques}
          onClose={() => { setShowValidModal(false); setSelected(null); }}
          onDone={() => { setShowValidModal(false); setSelected(null); load(); }}
        />
      )}

      {/* ── Modal nouveau transfert ── */}
      {showNouveauModal && (
        <NouveauTransfertModal
          boutiques={boutiques}
          onClose={() => setShowNouveauModal(false)}
          onDone={() => { setShowNouveauModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Modal : Traiter une demande boutique ─────────────────────────────────────

// Extrait id_produit et reference depuis un texte comme
// "Demande transfert pièce 1.0114 (BAGB21) pour vente client"
function parseRefFromComment(txt) {
  if (!txt) return null;
  const m = txt.match(/pi[eè]ce\s+(\S+)\s+\(([^)]+)\)/i);
  if (m) return { id_produit: m[1], reference: m[2] };
  // Essai sans parenthèses : "pièce BAGB21"
  const m2 = txt.match(/pi[eè]ce\s+(\S+)/i);
  if (m2) return { id_produit: null, reference: m2[1] };
  return null;
}

function ValidModal({ transfert, boutiques, onClose, onDone }) {
  // Pièce identifiée = bijou_id connu OU référence parseable dans le commentaire
  const parsedRef = parseRefFromComment(transfert.commentaire);
  const pieceConnueDirecte = !!(transfert.bijou_id || transfert.bijou_nom);

  const [sourceBoutiqueId, setSourceBoutiqueId] = useState('all');
  const [search, setSearch]     = useState('');
  const [dSearch, setDSearch]   = useState('');
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});  // { bijou.id: piece }
  const [saving, setSaving]     = useState(false);

  // Auto-recherche si référence parsée dans le commentaire
  const [autoPiece, setAutoPiece] = useState(null);   // pièce trouvée automatiquement
  const [autoLoading, setAutoLoading] = useState(false);

  useEffect(() => {
    if (pieceConnueDirecte || !parsedRef) return;
    // Chercher automatiquement la pièce par id_produit ou reference
    setAutoLoading(true);
    const searchTerm = parsedRef.id_produit || parsedRef.reference;
    transfertsAPI.getPiecesDisponibles(undefined, searchTerm)
      .then(res => {
        const allPieces = (res.data || []).flatMap(g => g.pieces || []);
        // Chercher correspondance exacte id_produit
        const exact = allPieces.find(p =>
          p.id_produit === parsedRef.id_produit ||
          (g => g.reference_article === parsedRef.reference)(res.data?.find(g => g.pieces?.includes(p)) || {})
        );
        // Fallback : première pièce du groupe avec la bonne référence
        const byRef = !exact && (res.data || []).find(g => g.reference_article === parsedRef.reference);
        const piece = exact || (byRef ? byRef.pieces?.[0] : null) || allPieces[0] || null;
        setAutoPiece(piece || null);
      })
      .catch(() => setAutoPiece(null))
      .finally(() => setAutoLoading(false));
  }, []); // eslint-disable-line

  const pieceConnue = pieceConnueDirecte || !!autoPiece || autoLoading;

  useEffect(() => {
    if (pieceConnue) return;
    const t = setTimeout(() => setDSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, pieceConnue]);

  const loadPieces = useCallback(async () => {
    if (pieceConnue) return;
    setLoading(true);
    try {
      const param = sourceBoutiqueId === 'all' ? undefined : sourceBoutiqueId;
      const res = await transfertsAPI.getPiecesDisponibles(param, dSearch);
      setGroups(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sourceBoutiqueId, dSearch, pieceConnue]);

  useEffect(() => { loadPieces(); }, [loadPieces]);

  const toggle = (piece) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[piece.id]) delete next[piece.id];
      else next[piece.id] = piece;
      return next;
    });
  };

  const selCount = Object.keys(selected).length;

  // Validation quand pièce déjà connue (bijou_id, bijou_nom ou autoPiece)
  const handleValiderPieceConnue = async () => {
    setSaving(true);
    try {
      let payload = {};
      if (transfert.bijou_id) {
        payload = { bijou_ids: [transfert.bijou_id] };
      } else if (autoPiece?.id) {
        payload = { bijou_ids: [autoPiece.id] };
      }
      await transfertsAPI.valider(transfert.id, payload);
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  const handleValider = async () => {
    if (selCount === 0) { alert('Sélectionnez au moins une pièce à envoyer'); return; }
    setSaving(true);
    try {
      await transfertsAPI.valider(transfert.id, { bijou_ids: Object.keys(selected).map(Number) });
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  const handleValiderSansPiece = async () => {
    if (!window.confirm('Valider sans assigner de pièce précise ?')) return;
    setSaving(true);
    try {
      await transfertsAPI.valider(transfert.id, {});
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  // ── CAS 1 : pièce déjà identifiée → confirmation simple ──────────────────────
  if (pieceConnue) {
    // Infos pièce : soit depuis transfert direct, soit depuis autoPiece trouvée
    const nomPiece    = transfert.bijou_nom || autoPiece?.nom || autoPiece?.reference_article || parsedRef?.reference || '—';
    const refPiece    = transfert.reference_article || autoPiece?.reference_article || parsedRef?.reference || '';
    const idProduit   = autoPiece?.id_produit || parsedRef?.id_produit || '';
    const sourcePiece = autoPiece?.boutique_nom || transfert.boutique_source_nom || 'Central';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Confirmer l'envoi</h3>
              <p className="text-blue-200 text-sm mt-0.5">
                {sourcePiece} → {transfert.boutique_destination_nom}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5" /></button>
          </div>

          {/* Corps */}
          <div className="p-6 space-y-4">
            {/* Pièce identifiée */}
            {autoLoading ? (
              <div className="flex items-center justify-center gap-3 py-6">
                <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
                <span className="text-sm text-gray-500">Recherche de la pièce <strong>{parsedRef?.id_produit || parsedRef?.reference}</strong>...</span>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-blue-500 font-medium uppercase tracking-wide mb-0.5">Pièce à envoyer</div>
                  <div className="font-bold text-gray-900">{nomPiece}</div>
                  {idProduit && idProduit !== refPiece && (
                    <div className="text-sm font-mono text-blue-700 mt-0.5">{idProduit}</div>
                  )}
                  {refPiece && (
                    <div className="text-xs text-gray-500 mt-0.5">Réf. {refPiece}</div>
                  )}
                  {autoPiece && (
                    <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Pièce trouvée en stock · {sourcePiece}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Trajet */}
            <div className="flex items-center gap-3 text-sm">
              <div className="flex-1 text-center">
                <div className="text-xs text-gray-400 mb-1">Depuis</div>
                <div className="font-semibold text-gray-800 flex items-center justify-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-red-400" />
                  {transfert.boutique_source_nom || 'Central'}
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 text-center">
                <div className="text-xs text-gray-400 mb-1">Vers</div>
                <div className="font-semibold text-gray-800 flex items-center justify-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-green-500" />
                  {transfert.boutique_destination_nom}
                </div>
              </div>
            </div>

            {/* Client à récupérer */}
            {transfert.client_nom && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <UserCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <div>
                  <span className="text-xs text-green-600 font-medium">Client qui récupère</span>
                  <div className="text-sm font-semibold text-gray-800">{transfert.client_nom}</div>
                </div>
              </div>
            )}

            {/* Raison */}
            {transfert.commentaire && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                📝 {transfert.commentaire}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
              Annuler
            </button>
            <button
              onClick={handleValiderPieceConnue}
              disabled={saving || autoLoading}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {autoLoading ? 'Recherche...' : 'Envoyer la pièce'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CAS 2 : demande générique → sélection manuelle de pièce(s) ───────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold">Traiter la demande</h3>
            <p className="text-blue-200 text-sm mt-0.5">
              {transfert.boutique_destination_nom} demande :&nbsp;
              <span className="font-semibold text-white">{transfert.commentaire || 'stock de bijoux'}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        {/* Filtres */}
        <div className="px-6 py-3 border-b border-gray-100 flex gap-3 items-center flex-shrink-0">
          <select
            value={sourceBoutiqueId}
            onChange={e => setSourceBoutiqueId(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">Tout le stock</option>
            <option value="0">Stock central</option>
            {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
          </select>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un bijou..."
              className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {selCount > 0 && (
            <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
              {selCount} sélectionnée{selCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Liste pièces */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="h-5 w-5 text-blue-500 animate-spin" /></div>
          ) : groups.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">Aucune pièce disponible</p>
          ) : groups.map(group => {
            const gk = group.reference_article || group.nom;
            const isExp = expanded[gk];
            const allSel = group.pieces.every(p => selected[p.id]);
            const someSel = group.pieces.some(p => selected[p.id]);

            const toggleGroup = () => {
              setSelected(prev => {
                const next = { ...prev };
                if (allSel) group.pieces.forEach(p => delete next[p.id]);
                else group.pieces.forEach(p => { next[p.id] = p; });
                return next;
              });
            };

            return (
              <div key={gk} className="border border-gray-100 rounded-xl overflow-hidden">
                <div
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpanded(prev => ({ ...prev, [gk]: !prev[gk] }))}
                >
                  <input type="checkbox" checked={allSel}
                    ref={el => el && (el.indeterminate = someSel && !allSel)}
                    onChange={toggleGroup} onClick={e => e.stopPropagation()}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm">{group.reference_article || group.nom}</div>
                    <div className="text-xs text-gray-400">{group.nom} · {group.collection_nom || 'Sans collection'}</div>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{group.pieces.length} pièce{group.pieces.length > 1 ? 's' : ''}</span>
                  <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                </div>
                {isExp && group.pieces.map(piece => {
                  const isSel = !!selected[piece.id];
                  return (
                    <label key={piece.id} className={`flex items-center gap-3 px-4 py-2 cursor-pointer border-t border-gray-50 ${isSel ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={isSel} onChange={() => toggle(piece)} className="h-4 w-4 text-blue-600 rounded" />
                      <div className="flex-1 text-sm font-medium text-gray-800">{piece.id_produit || `#${piece.id}`}</div>
                      {piece.couleur_or && <div className="text-xs text-gray-400">{piece.couleur_or}</div>}
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{piece.boutique_nom || 'Central'}
                      </div>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <button onClick={handleValiderSansPiece} disabled={saving} className="text-sm text-gray-500 hover:text-gray-700 underline">
            Valider sans assigner de pièce
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
            <button
              onClick={handleValider}
              disabled={saving || selCount === 0}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer {selCount > 0 ? `(${selCount} pièce${selCount > 1 ? 's' : ''})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal : Nouveau transfert (initié par central) ──────────────────────────

function NouveauTransfertModal({ boutiques, onClose, onDone }) {
  const [step, setStep]           = useState(1); // 1 = select dest, 2 = select pieces
  const [destId, setDestId]       = useState('');
  const [sourceBoutiqueId, setSrc] = useState('all');
  const [search, setSearch]       = useState('');
  const [dSearch, setDSearch]     = useState('');
  const [groups, setGroups]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [expanded, setExpanded]   = useState({});
  const [selected, setSelected]   = useState({});
  const [notes, setNotes]         = useState('');
  const [client, setClient]       = useState(null);   // { id, nom, telephone }
  const [venduAvance, setVenduAvance] = useState(false);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadPieces = useCallback(async () => {
    if (step !== 2) return;
    setLoading(true);
    try {
      const param = sourceBoutiqueId === 'all' ? undefined : sourceBoutiqueId;
      const res = await transfertsAPI.getPiecesDisponibles(param, dSearch);
      setGroups(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [step, sourceBoutiqueId, dSearch]);

  useEffect(() => { loadPieces(); }, [loadPieces]);

  const toggle = (piece) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[piece.id]) delete next[piece.id]; else next[piece.id] = piece;
      return next;
    });
  };

  const selCount = Object.keys(selected).length;

  const handleEnvoyer = async () => {
    if (selCount === 0 || !destId) return;
    setSaving(true);
    try {
      await transfertsAPI.centralCreate({
        bijou_ids: Object.keys(selected).map(Number),
        boutique_destination_id: parseInt(destId),
        notes,
        client_id: client?.id || null,
        vendu_avance: venduAvance,
      });
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  const destName = boutiques.find(b => b.id === parseInt(destId))?.nom || 'la boutique';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold">Nouveau transfert</h3>
            <p className="text-purple-200 text-sm">Central → {step === 2 ? destName : '...'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Boutique destination *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {boutiques.map(b => (
                  <button
                    key={b.id}
                    onClick={() => setDestId(String(b.id))}
                    className={`flex items-center gap-3 p-3 border-2 rounded-xl text-left transition-all ${
                      destId === String(b.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${destId === String(b.id) ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <MapPin className={`h-4 w-4 ${destId === String(b.id) ? 'text-purple-600' : 'text-gray-500'}`} />
                    </div>
                    <span className="font-medium text-sm text-gray-800">{b.nom}</span>
                    {destId === String(b.id) && <Check className="h-4 w-4 text-purple-600 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Raison du transfert..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
              />
            </div>

            {/* Client destinataire (optionnel) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Client destinataire <span className="font-normal text-gray-400">(optionnel)</span>
              </label>
              <ClientSearchInline value={client} onChange={setClient} />
            </div>

            {/* Vendu à l'avance */}
            {client && (
              <label className="flex items-start gap-3 cursor-pointer">
                <div className="relative mt-0.5">
                  <input type="checkbox" checked={venduAvance} onChange={e => setVenduAvance(e.target.checked)} className="sr-only" />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${venduAvance ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                    {venduAvance && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    Bijou déjà payé / vendu à l'avance
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Réservé pour ce client dès réception</div>
                </div>
              </label>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm">Annuler</button>
              <button onClick={() => setStep(2)} disabled={!destId}
                className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                Choisir les pièces <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="px-6 py-3 border-b border-gray-100 flex gap-3 items-center flex-shrink-0">
              <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                ← Changer destination
              </button>
              <select value={sourceBoutiqueId} onChange={e => setSrc(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400">
                <option value="all">Tout le stock</option>
                <option value="0">Stock central</option>
                {boutiques.filter(b => b.id !== parseInt(destId)).map(b =>
                  <option key={b.id} value={b.id}>{b.nom}</option>)}
              </select>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400" />
              </div>
              {selCount > 0 && (
                <span className="bg-purple-600 text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                  {selCount} sélectionnée{selCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="h-5 w-5 text-purple-500 animate-spin" /></div>
              ) : groups.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Aucune pièce disponible</p>
              ) : groups.map(group => {
                const gk = group.reference_article || group.nom;
                const isExp = expanded[gk];
                const allSel = group.pieces.every(p => selected[p.id]);
                const someSel = group.pieces.some(p => selected[p.id]);

                const toggleGroup = () => {
                  setSelected(prev => {
                    const next = { ...prev };
                    if (allSel) group.pieces.forEach(p => delete next[p.id]);
                    else group.pieces.forEach(p => { next[p.id] = p; });
                    return next;
                  });
                };

                return (
                  <div key={gk} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpanded(prev => ({ ...prev, [gk]: !prev[gk] }))}>
                      <input type="checkbox" checked={allSel}
                        ref={el => el && (el.indeterminate = someSel && !allSel)}
                        onChange={toggleGroup} onClick={e => e.stopPropagation()}
                        className="h-4 w-4 text-purple-600 rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm">{group.reference_article || group.nom}</div>
                        <div className="text-xs text-gray-400">{group.nom} · {group.collection_nom || 'Sans collection'}</div>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{group.pieces.length} pièce{group.pieces.length > 1 ? 's' : ''}</span>
                      <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                    </div>
                    {isExp && group.pieces.map(piece => {
                      const isSel = !!selected[piece.id];
                      return (
                        <label key={piece.id} className={`flex items-center gap-3 px-4 py-2 cursor-pointer border-t border-gray-50 ${isSel ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={isSel} onChange={() => toggle(piece)} className="h-4 w-4 text-purple-600 rounded" />
                          <div className="flex-1 text-sm font-medium text-gray-800">{piece.id_produit || `#${piece.id}`}</div>
                          {piece.couleur_or && <div className="text-xs text-gray-400">{piece.couleur_or}</div>}
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />{piece.boutique_nom || 'Central'}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
              <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm">Annuler</button>
              <button onClick={handleEnvoyer} disabled={saving || selCount === 0}
                className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer vers {destName} {selCount > 0 ? `(${selCount})` : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  VUE BOUTIQUE
// ═══════════════════════════════════════════════════════════════════════════════

function BoutiqueView({ myBoutiqueId, myBoutiqueName }) {
  const [transferts, setTransferts] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [showModal, setShowModal]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await transfertsAPI.getAll({ boutique_id: myBoutiqueId });
      // Exclure l'historique — boutique ne voit que les demandes actives
      const data = Array.isArray(res.data) ? res.data : [];
      setTransferts(data.filter(t => t.statut !== 'recu' && t.statut !== 'annule'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [myBoutiqueId]);

  useEffect(() => { load(); }, [load]);

  const handleAnnuler = async (t) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await transfertsAPI.annuler(t.id);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const en_attente = transferts.filter(t => t.statut === 'en_attente');
  const en_transit = transferts.filter(t => t.statut === 'valide');

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-2.5 rounded-xl shadow">
            <ArrowLeftRight className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mes demandes de transfert</h2>
            <p className="text-sm text-gray-500">{myBoutiqueName || 'Boutique'} · Suivi de vos demandes en cours</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 shadow-sm transition-all"
        >
          <Plus className="h-4 w-4" />
          Nouvelle demande
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 text-purple-500 animate-spin" /></div>
      ) : transferts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 flex flex-col items-center text-center">
          <div className="bg-purple-100 p-4 rounded-2xl mb-4">
            <ArrowLeftRight className="h-10 w-10 text-purple-600" />
          </div>
          <h3 className="font-bold text-gray-800 text-lg">Aucune demande en cours</h3>
          <p className="text-gray-500 text-sm mt-1 mb-6">Faites une demande de transfert pour recevoir des bijoux depuis le central ou une autre boutique.</p>
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700">
            <Plus className="h-4 w-4" /> Faire une demande
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* En attente de traitement */}
          {en_attente.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                En attente de traitement par le central ({en_attente.length})
              </h3>
              <div className="space-y-2">
                {en_attente.map(t => (
                  <BoutiqueTransfertCard key={t.id} t={t} onAnnuler={handleAnnuler} />
                ))}
              </div>
            </div>
          )}

          {/* En transit */}
          {en_transit.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Truck className="h-4 w-4 text-blue-500" />
                En route vers vous ({en_transit.length})
              </h3>
              <div className="space-y-2">
                {en_transit.map(t => (
                  <BoutiqueTransfertCard key={t.id} t={t} onAnnuler={null} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <DemandeBoutiqueModal
          myBoutiqueId={myBoutiqueId}
          onClose={() => setShowModal(false)}
          onDone={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function BoutiqueTransfertCard({ t, onAnnuler }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
      <div className={`p-3 rounded-xl flex-shrink-0 ${t.statut === 'valide' ? 'bg-blue-100' : 'bg-amber-100'}`}>
        {t.statut === 'valide'
          ? <Truck className="h-5 w-5 text-blue-600" />
          : <Clock className="h-5 w-5 text-amber-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">
          {t.bijou_nom || <span className="italic text-gray-500">Demande en cours de traitement</span>}
        </div>
        {t.commentaire && <div className="text-sm text-gray-500 truncate">{t.commentaire}</div>}
        {/* Client badge */}
        {t.client_nom && (
          <div className="mt-1 inline-flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${t.vendu_avance ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
              <UserCircle className="h-3 w-3" />
              {t.client_nom}
              {t.vendu_avance ? ' · Payé' : ' · À récupérer'}
            </span>
          </div>
        )}
        <div className="text-xs text-gray-400 mt-0.5">
          Demandé le {fmt(t.date_demande)}
          {t.date_validation && ` · Validé le ${fmt(t.date_validation)}`}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatutBadge statut={t.statut} />
        {t.statut === 'en_attente' && onAnnuler && (
          <button onClick={() => onAnnuler(t)}
            className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Annuler">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Modal : Boutique crée une demande ────────────────────────────────────────

function DemandeBoutiqueModal({ myBoutiqueId, onClose, onDone }) {
  const [form, setForm] = useState({
    notes: '',
    quantite: 1,
    boutique_source_id: '',
  });
  const [client, setClient]     = useState(null);   // { id, nom, telephone }
  const [venduAvance, setVenduAvance] = useState(false);
  const [boutiques, setBoutiques] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    boutiquesAPI.getAll().then(r => setBoutiques(Array.isArray(r.data) ? r.data : [])).catch(err => console.error('Erreur:', err));
  }, []);

  const handleSubmit = async () => {
    if (!form.notes.trim()) { alert('Décrivez ce que vous souhaitez recevoir'); return; }
    setSaving(true);
    try {
      await transfertsAPI.demandeBoutique({
        boutique_destination_id: myBoutiqueId,
        boutique_source_id: form.boutique_source_id || null,
        quantite: parseInt(form.quantite) || 1,
        notes: form.notes,
        client_id: client?.id || null,
        vendu_avance: venduAvance,
      });
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <h3 className="text-lg font-bold">Nouvelle demande de transfert</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Que souhaitez-vous recevoir ? *
            </label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Ex: bague réf. JSR-001 taille 54, collier de la collection Amira..."
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Quantité</label>
              <input type="text" inputMode="decimal" onFocus={e => e.target.select()} min={1} value={form.quantite}
                onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Depuis</label>
              <select value={form.boutique_source_id}
                onChange={e => setForm(f => ({ ...f, boutique_source_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-400">
                <option value="">Stock central</option>
                {boutiques.filter(b => b.id !== myBoutiqueId).map(b =>
                  <option key={b.id} value={b.id}>{b.nom}</option>)}
              </select>
            </div>
          </div>

          {/* Client destinataire (optionnel) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Client destinataire <span className="font-normal text-gray-400">(optionnel)</span>
            </label>
            <ClientSearchInline value={client} onChange={setClient} />
          </div>

          {/* Vendu à l'avance */}
          {client && (
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative mt-0.5">
                <input type="checkbox" checked={venduAvance} onChange={e => setVenduAvance(e.target.checked)}
                  className="sr-only" />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${venduAvance ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                  {venduAvance && <Check className="h-3 w-3 text-white" />}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  Bijou déjà payé / vendu à l'avance
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Le bijou sera réservé pour ce client dès réception
                </div>
              </div>
            </label>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Votre demande sera transmise au central qui organisera le transfert.
            </p>
          </div>
        </div>

        <div className="px-6 pb-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm">Annuler</button>
          <button onClick={handleSubmit} disabled={saving || !form.notes.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer la demande
          </button>
        </div>
      </div>
    </div>
  );
}
